import type { HqApi, UserProfile } from '@hq/api-sdk';
import { DownOutlined, KeyOutlined, LogoutOutlined, QrcodeOutlined, UserOutlined } from '@ant-design/icons';
import { Avatar, Dropdown, Space, Typography, type MenuProps } from 'antd';
import { useState } from 'react';
import ProfileDrawer from './ProfileDrawer';
import type { AccountProfileView, ProfileTab } from './types';

export interface UserMenuProps {
  hq: HqApi;
  profile: AccountProfileView;
  /** 退出登录（各端自己 clear 会话 + 跳登录页）。 */
  onLogout: () => void;
  /** 资料更新后回传最新 Profile，各端 patchProfile 刷新头部。 */
  onProfileUpdated?: (profile: UserProfile) => void;
  /** 头像右侧的姓名/机构文本（窄屏由 compact 控制）。 */
  subtitle?: string | null;
  /** 追加在「退出」之前的菜单项，例如 system 端的「手机打开」。 */
  extraItems?: MenuProps['items'];
  /** 窄屏（<768px）只显示头像，不显示姓名。 */
  compact?: boolean;
  /** 展示「我的二维码」入口与页签（目前仅院方端开启）。 */
  showSelfQr?: boolean;
}

/**
 * 头部账户菜单（五端共用）：头像 + 姓名 → 下拉「个人信息 / 修改密码 / 退出」。
 * 把个人账户能力收敛到一处，避免各门户各写一套下拉与表单。
 */
export default function UserMenu({
  hq,
  profile,
  onLogout,
  onProfileUpdated,
  subtitle,
  extraItems,
  compact,
  showSelfQr
}: UserMenuProps) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<ProfileTab>('info');

  const avatarText = (profile.name ?? profile.userCode ?? '?').trim().slice(0, 1);

  const items: MenuProps['items'] = [
    { key: 'who', disabled: true, label: <UserBrief profile={profile} /> },
    { type: 'divider' },
    { key: 'info', icon: <UserOutlined />, label: '个人信息' },
    { key: 'password', icon: <KeyOutlined />, label: '修改密码' },
    ...(showSelfQr ? [{ key: 'qrcode', icon: <QrcodeOutlined />, label: '我的二维码' }] : []),
    ...(extraItems ?? []),
    { type: 'divider' },
    { key: 'logout', icon: <LogoutOutlined />, label: '退出登录', danger: true }
  ];

  return (
    <>
      <Dropdown
        trigger={['click']}
        menu={{
          items,
          onClick: ({ key }) => {
            if (key === 'logout') {
              onLogout();
              return;
            }
            if (key === 'info' || key === 'password' || key === 'qrcode') {
              setTab(key as ProfileTab);
              setOpen(true);
            }
          }
        }}
      >
        <Space size={8} style={{ cursor: 'pointer', lineHeight: 1.2 }}>
          <Avatar size={28} src={profile.avatarUrl || undefined} style={{ background: '#1677ff' }}>
            {avatarText}
          </Avatar>
          {!compact && (
            <Typography.Text ellipsis style={{ maxWidth: 220 }}>
              {profile.name ?? '未登录'}
              {subtitle ? <Typography.Text type="secondary">（{subtitle}）</Typography.Text> : null}
            </Typography.Text>
          )}
          {!compact && <DownOutlined style={{ fontSize: 10, color: '#999' }} />}
        </Space>
      </Dropdown>

      <ProfileDrawer
        hq={hq}
        open={open}
        tab={tab}
        profile={profile}
        onClose={() => setOpen(false)}
        onUpdated={onProfileUpdated}
        showSelfQr={showSelfQr}
      />
    </>
  );
}

function UserBrief({ profile }: { profile: AccountProfileView }) {
  return (
    <div style={{ lineHeight: 1.4, cursor: 'default' }}>
      <div>
        <Typography.Text strong>{profile.name ?? profile.userCode ?? '-'}</Typography.Text>
        {profile.userCode ? <Typography.Text type="secondary"> · {profile.userCode}</Typography.Text> : null}
      </div>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {[profile.orgName, profile.deptName].filter(Boolean).join(' / ') || profile.mobile || '-'}
      </Typography.Text>
    </div>
  );
}
